import { Button, Modal } from '@shared/ui/kit';
import stylex from '@stylexjs/stylex';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { XmarkIcon } from '@shared/assets/icons';
import { AdditionalGuestTab } from './AdditionalGuestTab';
import { MainGuestTab } from './MainGuestTab';
import { FormBodyWithGuests } from './FormBodyWithGuests';
import { AdditionalGuestsFromFormBody } from './AdditionalGuestsFromFormBody';
import { getMainGuestFromBodyForm, getGuestsFromBodyForm } from './lib/getMainGuestFromForm';
import { buildSecondarySubmissionInfo } from './lib/buildSecondarySubmissionInfo';
import {
  validateMainGuestSimple,
  buildMainGuestOnlySubmissionInfo,
  type MainGuestSimpleErrors,
} from './lib/validateMainGuestSimple';
import EmployeeSwitcher from '@entities/employee-switcher';
import { useToast } from '@shared/hooks/useToast';
import { useSubmitFormManual } from '@widgets/segments-tab/api/useSubmitFormManual';
import { useAddSecondaryGuest } from '@widgets/segments-tab/api/useAddSecondaryGuest';
import { styles } from './styles';
import { useCustomScrollbar } from '@shared/hooks/useCustomScrollbar';
import { scrollbarStyles } from './scrollbar/styles';

import type { Form, GuestInfo } from '@entities/leadCollector';
import { typography } from '@shared/tokens/typography.stylex';
import type { MainGuestDropdownOption } from '@widgets/segments-tab';

const TAB_LABELS = ['Основной гость', 'Дополнительный гость'];

export interface CommercialBlockParticipantsModalProps {
  open: boolean;
  onClose: () => void;
  onlyMainGuest?: boolean;
  formData?: Form | null;
  eventId?: number;
  ticketSegmentId?: number;
  mainGuestOptions?: MainGuestDropdownOption[];
  onSuccess?: () => void;
}

export const CommercialBlockParticipantsModal: FC<CommercialBlockParticipantsModalProps> = ({
  open,
  onClose,
  onlyMainGuest = false,
  formData,
  eventId,
  ticketSegmentId,
  mainGuestOptions = [],
  onSuccess,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedMainGuestOption, setSelectedMainGuestOption] = useState<MainGuestDropdownOption | null>(null);
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [patronymic, setPatronymic] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [mainGuestErrors, setMainGuestErrors] = useState<MainGuestSimpleErrors>({});
  const validateFormBodyRef = useRef<(() => Promise<boolean>) | null>(null);
  const collectSubmissionInfoRef = useRef<(() => string | null) | null>(null);
  const tab2AdultGuestsRef = useRef<GuestInfo[]>([]);
  const tab2ChildrenGuestsRef = useRef<GuestInfo[]>([]);
  const { scrollRef: modalScrollRef, customScrollbar } = useCustomScrollbar<HTMLDivElement>(open, activeIndex);
  const { showToast } = useToast();
  const { submitFormManual } = useSubmitFormManual();
  const { addSecondaryGuest } = useAddSecondaryGuest();

  const setValidateFormBody = useCallback((fn?: () => Promise<boolean>) => {
    validateFormBodyRef.current = fn ?? null;
  }, []);

  const setCollectSubmissionInfo = useCallback((fn?: () => string | null) => {
    collectSubmissionInfoRef.current = fn ?? null;
  }, []);

  const mainGuestFromForm = formData?.bodyForm ? getMainGuestFromBodyForm(formData.bodyForm) : null;
  const showFormBody = !onlyMainGuest && !!mainGuestFromForm;
  const showExtraBlock = !!(formData?.maxExtraAdults ?? 0) || !!(formData?.maxExtraChildren ?? 0);
  const tabLabels = showFormBody && formData && !showExtraBlock ? ['Основной гость'] : TAB_LABELS;
  const guestsFromForm = formData?.bodyForm ? getGuestsFromBodyForm(formData.bodyForm) : null;
  const maxExtraAdults = formData?.maxExtraAdults ?? 0;
  const maxExtraChildren = formData?.maxExtraChildren ?? 0;

  const needValidationTab1 = showFormBody && (activeIndex === 0 || tabLabels.length === 1);
  const needValidationTab2 = activeIndex === 1 && tabLabels.length > 1 && showFormBody && !!formData && showExtraBlock;
  const needValidationOnlyMainGuest = onlyMainGuest && !showFormBody;

  const showSubmitFormManualError = useCallback(
    (
      error: { message?: string; graphQLErrors?: Array<{ message?: string }> } | undefined,
      guestLastName?: string,
      guestFirstName?: string
    ) => {
      const message = error?.message ?? error?.graphQLErrors?.[0]?.message ?? '';
      if (!/already exist/i.test(message)) {
        showToast('Ошибка при сохранении', 'error');
        return;
      }
      const name = [guestLastName, guestFirstName].filter(Boolean).join(' ').trim();
      showToast(name ? `Гость (${name}) уже добавлен в мероприятие` : 'Гость уже добавлен в мероприятие', 'error');
    },
    [showToast]
  );

  useEffect(() => {
    if (open) setMainGuestErrors({});
  }, [open]);

  const handleClearForm = () => {
    setLastName('');
    setFirstName('');
    setPatronymic('');
    setPhone('');
    setEmail('');
  };

  const handleSubmitOnlyMainGuest = useCallback(async () => {
    const errors = await validateMainGuestSimple({
      lastName,
      firstName,
      patronymic,
      phone,
      email,
    });
    const hasErrors = Object.values(errors).some((e) => !!e);
    setMainGuestErrors(errors);
    if (hasErrors) return;

    if (eventId == null || ticketSegmentId === undefined) {
      showToast('Ошибка при отправке данных', 'error');
      return;
    }
    const submissionInfo = buildMainGuestOnlySubmissionInfo({
      lastName,
      firstName,
      patronymic,
      phone,
      email,
    });
    const result = await submitFormManual({
      input: {
        eventId,
        submissionInfo,
        ticketSegmentId: ticketSegmentId || null,
      },
    });
    if (result.error || !result.data?.submitFormManual) {
      showToast('Ошибка при сохранении', 'error');
      return;
    }
    onSuccess?.();
    onClose();
    handleClearForm();
  }, [
    lastName,
    firstName,
    patronymic,
    phone,
    email,
    eventId,
    ticketSegmentId,
    showToast,
    showSubmitFormManualError,
    submitFormManual,
    onSuccess,
    onClose,
  ]);

  const handleSubmit = async () => {
    if (needValidationOnlyMainGuest) {
      await handleSubmitOnlyMainGuest();
      return;
    }

    const needValidation = needValidationTab1 || needValidationTab2;
    if (!needValidation || !validateFormBodyRef.current) {
      onClose();
      return;
    }
    const valid = await validateFormBodyRef.current();
    if (!valid) return;

    if (needValidationTab2) {
      if (!selectedMainGuestOption?.ticketId) {
        showToast('Выберите основного гостя', 'error');
        return;
      }
      if (!eventId) {
        showToast('Ошибка при отправке данных', 'error');
        return;
      }
      const submissionInfo = buildSecondarySubmissionInfo(tab2AdultGuestsRef.current, tab2ChildrenGuestsRef.current);
      const result = await addSecondaryGuest({
        input: {
          eventId,
          parentTicketId: selectedMainGuestOption.ticketId,
          submissionInfo,
        },
      });
      if (result.error || !result.data?.addSecondaryGuest) {
        showToast('Ошибка при отправке данных', 'error');
        return;
      }
      onSuccess?.();
      onClose();
      return;
    }

    const submissionInfo = collectSubmissionInfoRef.current?.();
    if (!submissionInfo) {
      showToast('Ошибка при подготовке данных', 'error');
      return;
    }

    if (eventId == null || ticketSegmentId === undefined) {
      showToast('Ошибка при отправке данных', 'error');
      return;
    }

    const result = await submitFormManual({
      input: {
        eventId,
        submissionInfo,
        ticketSegmentId: ticketSegmentId || null,
      },
    });
    if (result.error) {
      showSubmitFormManualError(result.error, lastName, firstName);
      return;
    }
    if (!result.data?.submitFormManual) {
      showToast('Ошибка при сохранении', 'error');
      return;
    }
    onSuccess?.();
    onClose();
  };

  return createPortal(
    <Modal
      active={open}
      settingsContainer={{ height: 'auto', minWidth: '480px', padding: '0px' }}
      settingsHeader={{ padding: '0px' }}
      settingsFooter={{ padding: '0px' }}
    >
      <header {...stylex.props(styles.modalHeader)}>
        <h2 {...stylex.props(styles.modalTitle)}>Добавить участников</h2>
        <XmarkIcon onClick={onClose} size="24" {...stylex.props(styles.closeButton)} />
      </header>
      <main {...stylex.props(styles.modalBody)}>
        {!onlyMainGuest && (
          <div {...stylex.props(styles.modalSwitcherRow)}>
            <EmployeeSwitcher blocks={tabLabels} activeIndex={activeIndex} setActiveIndex={setActiveIndex} />
          </div>
        )}
        <div {...stylex.props(styles.modalSectionsScrollWrapper)}>
          <div {...stylex.props(scrollbarStyles.host)}>
            <div ref={modalScrollRef} {...stylex.props(scrollbarStyles.scroll)}>
              <div {...stylex.props(styles.modalSections)}>
                {(activeIndex === 0 || tabLabels.length === 1) && showFormBody && formData && mainGuestFromForm && (
                  <FormBodyWithGuests
                    formData={formData}
                    mainGuest={mainGuestFromForm}
                    registerValidate={setValidateFormBody}
                    registerCollectSubmissionInfo={setCollectSubmissionInfo}
                  />
                )}
                {(activeIndex === 0 || tabLabels.length === 1) && !showFormBody && (
                  <MainGuestTab
                    lastName={lastName}
                    setLastName={setLastName}
                    firstName={firstName}
                    setFirstName={setFirstName}
                    patronymic={patronymic}
                    setPatronymic={setPatronymic}
                    phone={phone}
                    setPhone={setPhone}
                    email={email}
                    setEmail={setEmail}
                    onlyMainGuestSection={onlyMainGuest}
                    errors={mainGuestErrors}
                  />
                )}
                {activeIndex === 1 &&
                  tabLabels.length > 1 &&
                  (showFormBody && formData && showExtraBlock ? (
                    <AdditionalGuestTab
                      mainGuestOptions={mainGuestOptions}
                      value={selectedMainGuestOption}
                      onMainGuestChange={setSelectedMainGuestOption}
                      extraGuestsSection={
                        <AdditionalGuestsFromFormBody
                          maxExtraAdults={maxExtraAdults}
                          maxExtraChildren={maxExtraChildren}
                          adultTemplate={guestsFromForm?.adultGuest?.[0] ?? null}
                          childrenTemplate={guestsFromForm?.childrenGuest?.[0] ?? null}
                          adultGuestTitle={guestsFromForm?.adultGuestTitle}
                          childGuestTitle={guestsFromForm?.childGuestTitle}
                          adultButtonTitle={guestsFromForm?.adultButtonTitle}
                          childButtonTitle={guestsFromForm?.childButtonTitle}
                          registerValidate={setValidateFormBody}
                          onChangeGuests={(adult, children) => {
                            tab2AdultGuestsRef.current = adult;
                            tab2ChildrenGuestsRef.current = children;
                          }}
                        />
                      }
                    />
                  ) : (
                    <AdditionalGuestTab mainGuestOptions={mainGuestOptions} />
                  ))}
              </div>
            </div>
            {customScrollbar.visible && (
              <div {...stylex.props(scrollbarStyles.track)}>
                <div
                  {...stylex.props(scrollbarStyles.thumb)}
                  style={{
                    height: `${customScrollbar.thumbHeight}px`,
                    transform: `translateY(${customScrollbar.thumbTop}px)`,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </main>
      <footer {...stylex.props(styles.modalFooter)}>
        <Button
          size="small"
          variant="secondary"
          settings={{ padding: '8px 20px', fontSize: typography['fontSizeCaption-1'] }}
          onClick={onClose}
        >
          Отмена
        </Button>
        <Button
          size="small"
          variant="accent"
          settings={{ padding: '8px 20px', fontSize: typography['fontSizeCaption-1'] }}
          onClick={handleSubmit}
        >
          Добавить
        </Button>
      </footer>
    </Modal>,
    document.getElementById('modal') ?? document.body
  );
};
